import type { User } from '../types';

export type GreetingSlot = 'morning' | 'afternoon' | 'evening';

/**
 * Hour boundaries follow the Japanese convention, which is the default locale:
 * おはようございます until 11, こんにちは until 18, こんばんは after.
 */
export const GREETING_BOUNDARIES = { morning: 5, afternoon: 11, evening: 18 } as const;

export function greetingSlotAt(date: Date): GreetingSlot {
  const hour = date.getHours();
  if (hour >= GREETING_BOUNDARIES.evening || hour < GREETING_BOUNDARIES.morning) return 'evening';
  if (hour < GREETING_BOUNDARIES.afternoon) return 'morning';
  return 'afternoon';
}

/**
 * What to call the user, or nothing.
 *
 * Deliberately does NOT fall back to the email local part. Nextcloud's greeting
 * suppresses the name whenever it is still the login id, on the grounds that
 * "Good morning, admin" — or worse, a uuid — reads worse than a bare greeting.
 * Only a name a human chose is used.
 */
export function displayNameOf(user: Pick<User, 'fullName' | 'display_name'> | null): string {
  if (!user) return '';
  return surnameOf(user.fullName?.trim() || user.display_name?.trim() || '');
}

const CJK = /[぀-ヿ㐀-䶿一-鿿]/;

/**
 * The name to address someone by. Japanese business register is surname + さん
 * and the surname comes first; English puts it last. Greeting a Japanese
 * colleague by their given name is as wrong as calling an English one by
 * their surname alone, so the split follows the script of the name itself —
 * both full-width and half-width spaces separate the parts.
 */
export function surnameOf(fullName: string): string {
  const parts = fullName.trim().split(/[\s　]+/).filter(Boolean);
  if (parts.length <= 1) return parts[0] ?? '';
  return CJK.test(fullName) ? parts[0] : parts[parts.length - 1];
}
